import type { SubscriptionResponse } from './types';

/**
 * Thrown when a subscription payment did not complete (declined card or failed authentication).
 * Carries a customer-safe message so callers can show the specific reason and offer a retry
 * instead of a misleading success.
 *
 * Kept in its own leaf module (not the billing barrel) so consumers can import it without pulling
 * in the whole `@stores/billing` graph, which would create a circular dependency via the RootStore.
 */
export class PaymentFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PaymentFailedError';
  }
}

export const GENERIC_PAYMENT_FAILURE_MESSAGE =
  'Your payment could not be completed. Please try a different payment method.';

// Returns the customer-safe decline reason when the error is a PaymentFailedError, else the given
// fallback — so every surface shows the specific reason where one exists.
export function preferPaymentFailureReason(error: unknown, fallback: string): string {
  return error instanceof PaymentFailedError ? error.message : fallback;
}

// A 402 from the billing API: the charge was declined with a customer-safe reason in the body —
// and, for atomic creates, no subscription was persisted at all.
export function isPaymentRequiredError(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 402;
}

// A subscription response signals a failed/incomplete payment when the payment intent still needs a
// payment method or a decline reason came back — with Stripe pending updates the subscription stays
// `active` on failure, so status alone can't be the signal. The only status treated as a failure is
// `incomplete` (a new subscription whose first invoice wasn't paid): a recovered-but-still-past_due
// subscription can legitimately complete deferred changes, so past_due is not a failure here.
export function isSubscriptionPaymentIncomplete(data: SubscriptionResponse): boolean {
  if (data.declineReason || data.paymentStatus === 'requires_payment_method') {
    return true;
  }
  return data.status === 'incomplete';
}
